import type { DocPage } from '../types'
import { media } from '../media'


export const postgis: DocPage = {
  id: 'postgis',
  title: 'PostGIS',
  description:
    'Connect to PostGIS with host, credentials, and database multi-select; apply filters; validate scratch catalog fields; and configure the style export accordion.',
  category: 'Formats',
  sections: [
    { id: 'overview', title: 'Overview' },
    { id: 'connection', title: 'Source connection' },
    { id: 'target', title: 'Target connection' },
    { id: 'databases', title: 'Database multi-select' },
    { id: 'filters', title: 'Filters' },
    { id: 'scratch', title: 'Scratch catalog' },
    { id: 'logical', title: 'Logical objects' },
    { id: 'map', title: 'Map overview' },
    { id: 'styles', title: 'Style export accordion' },
    { id: 'card', title: 'Connection card labels' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'overview',
      text: 'Overview',
    },
    {
      type: 'paragraph',
      text: 'PostGIS is available as Source and Target. Typical flows: fetch databases, select one or more databases, load the model tree, optionally filter collections/attributes, map or scratch-write, and optionally export QGIS styles into files or the database layer_styles table.',
    },
    {
      type: 'figure',
      src: media.sourceConnectionPostgis,
      alt: 'PostGIS connection form',
      caption:
        'Figure: Source model connection for PostGIS — host, port, user, password, then proceed to database selection and model tree.',
      size: 'compact',
    },
    {
      type: 'figure',
      src: media.sourceModelTreePostgis,
      alt: 'PostGIS source model tree',
      caption:
        'Figure: PostGIS Source model tree after connection — hierarchical databases, schemas, tables/collections, and attributes.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'connection',
      text: 'Source connection',
    },
    {
      type: 'table',
      headers: ['Field', 'Description'],
      rows: [
        ['Host', 'Database server hostname or IP.'],
        ['Port', 'PostgreSQL/PostGIS port (commonly 5432).'],
        ['User', 'Database user.'],
        ['Password', 'Password for the user.'],
        ['Select database', 'Multi-select list populated after fetch.'],
        ['Submit', 'Accept the connection / proceed to model load.'],
        ['Cancel', 'Close without applying.'],
        ['Override Existing', 'Target option when writing should replace existing objects as offered by the form.'],
      ],
    },
    {
      type: 'paragraph',
      text: 'Actions such as Fetch DB / Model Tree retrieve databases and then the hierarchical collections for the selection.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'target',
      text: 'Target connection',
    },
    {
      type: 'paragraph',
      text: 'On Target → PostGIS → New, the Target Model Connection dialog lets you choose Mapped Transfer or Scratch Transfer, then enter host, port, credentials, and (for scratch) Database, Schema, Table (base), Override Existing, and Layer style export.',
    },
    {
      type: 'figure',
      src: media.targetConnectionPostgis,
      alt: 'PostGIS Target Model Connection dialog',
      caption:
        'Figure: Target Model Connection — Mapped vs Scratch Transfer, Host/Port/User/Password, Database/Schema/Table (base), Override Existing, and Layer style export.',
      size: 'compact',
    },
    {
      type: 'heading',
      level: 2,
      id: 'databases',
      text: 'Database multi-select',
    },
    {
      type: 'list',
      items: [
        'After connecting credentials, choose one or more databases in Select database.',
        'Validation: Please select at least one database.',
        'Multi-database selections merge visible collections into the model tree according to product rules for your connection.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'filters',
      text: 'Filters',
    },
    {
      type: 'paragraph',
      text: 'Use Add Filter / Change Filters (Collections and Attributes tabs) to limit schemas/tables/columns. Filters reduce model size and transfer scope. Combine with Map layer visibility when using spatial overview.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'scratch',
      text: 'Scratch catalog',
    },
    {
      type: 'paragraph',
      text: 'For scratch Target PostGIS, provide catalog coordinates for created tables. After a successful connect, the Target panel shows Scratch Transfer Details with Connection Successful and a summary of Host, Port, Database, Schema, Table (base), Override Existing, and layer style export settings. Use Change Connection to edit them.',
    },
    {
      type: 'figure',
      src: media.scratchTransferDetailsPostgis,
      alt: 'PostGIS Scratch Transfer Details card',
      caption:
        'Figure: Scratch Transfer Details for PostGIS Target — connection summary, Override Existing, and layer_styles export summary, with Change Connection.',
      size: 'compact',
    },
    {
      type: 'table',
      headers: ['Field', 'Description'],
      rows: [
        ['Database', 'Database where scratch tables are created.'],
        ['Schema', 'Schema for new tables.'],
        ['Table (base)', 'Base table name used when deriving scratch table names.'],
      ],
    },
    {
      type: 'callout',
      variant: 'warning',
      title: 'Validation',
      text: 'Scratch catalog fields are validated before transfer. Empty or invalid Database / Schema / Table (base) values block the run with form validation messages — fix them before opening Transfer Review.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'logical',
      text: 'Logical objects',
    },
    {
      type: 'paragraph',
      text: 'PostGIS supports logical collections, attributes, and enumerators. ENUM attributes require an enumerator. See Logical objects for field-level detail.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'map',
      text: 'Map overview',
    },
    {
      type: 'paragraph',
      text: 'Source PostGIS supports Tree / Map, spatial draw tools, and the Attribute Table. Hidden layers are excluded from transfer. See Map view & attribute table.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'styles',
      text: 'Style export accordion',
    },
    {
      type: 'paragraph',
      text: 'Accordion title: Layer style export (also shown as Layer style files after transfer). Configure export that runs after a successful transfer when enabled.',
    },
    {
      type: 'figure',
      src: media.styleExportAccordion,
      alt: 'Layer style export options',
      caption:
        'Figure: Layer style export — Export style sidecars (QML / SLD / XStyles), Write QGIS layer_styles in DB, schema, output folder, layer base name, and geometry type.',
      size: 'compact',
    },
    {
      type: 'table',
      headers: ['Option', 'Description'],
      rows: [
        ['Export style sidecars', 'Master toggle for writing style files.'],
        ['QML (QGIS)', 'Export QGIS QML sidecars.'],
        ['SLD (OGC)', 'Export OGC Styled Layer Descriptor files.'],
        ['XStyles JSON', 'Export SEPM XStyles JSON.'],
        ['Extra QML/SLD per style definition', 'Write additional files per definition (_def_*).'],
        ['Extra QML/SLD per target table', 'Also emit extras per target table (_tbl_*) when enabled.'],
        ['Write QGIS layer_styles in DB', 'Insert/update QGIS layer_styles in PostGIS.'],
        ['layer_styles schema', 'Schema hosting the layer_styles table (commonly public).'],
        ['Output folder', 'Filesystem folder for sidecar files (defaults to server working directory if empty).'],
        ['Layer base name', 'Base name used when composing file names.'],
        ['Geometry type (symbolizer)', 'Geometry type guiding symbolizer choice (for example LineString).'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'card',
      text: 'Connection card labels',
    },
    {
      type: 'paragraph',
      text: 'After connect, the summary card shows Host:, Port:, User:, and Database: for quick verification before mapping.',
    },
  ],
}
